import { useMuiMediaQuery } from '@/hooks/useMediaQuery';
import { roundNumber } from '@/utils/maths';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import json2mq from 'json2mq';
import { useRef, useState } from 'react';
import ActivityTotalOptions from '../components/ActivityTotalOptions';
import Head from '../components/Head';
import { NoData } from '../components/NoData';
import SkeletonLoader from '../components/SkeletonLoader';
import StackingOptions from '../components/StackingOptions';
import { useActivities } from '../hooks/useActivities';
import { useGetInitialValue } from '../hooks/useGetInitialValue';
import styles from '../style.module.css';
import { Stacking } from '../types';
import {
  black,
  blue,
  green,
  meanLineStyle,
  orange,
  titleStyle,
} from '../utils/common';
import { formatWeekNumber } from '../utils/date';
import {
  DataType,
  dataTypeLabels,
  dataTypeUnits,
  dataTypes,
} from '../utils/enum';
import { getTriathlonStats } from '../utils/getTriathlonStats';

const TriathlonChart = () => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const { activities, isLoading } = useActivities();
  const isMobile = useMuiMediaQuery(json2mq({ maxWidth: 600 }));
  const { initialValue } = useGetInitialValue();

  const [stacking, setStacking] = useState<Stacking>('normal');
  const [activityTotal, setActivityTotal] = useState<DataType>('moving_time');

  const handleRadioChangeActivityTotal = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setActivityTotal(value as DataType);
  };

  const handleRadioChangeStacking = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const newStacking = value === 'undefined' ? undefined : (value as Stacking);
    setStacking(newStacking);
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  const { runningStats, ridingStats, swimmingStats, allActivitiesStats } =
    getTriathlonStats(activities, activityTotal, isMobile, initialValue);

  const total = activities.reduce((acc, activity) => {
    switch (activityTotal) {
      case 'distance':
        return acc + activity.distance / 1000;
      case 'moving_time':
        return acc + activity.moving_time / 3600;
      case 'count':
        return acc + 1;
      case 'elevation_gain':
        return acc + activity.total_elevation_gain;
      case 'suffer_score':
        return acc + (activity.suffer_score || 0);
      default:
        return 0;
    }
  }, 0);

  const nbWeeks = Object.values(allActivitiesStats).filter((e) => e).length;
  const mean = roundNumber(total / nbWeeks);

  const categories = Object.keys(runningStats).map((yearWeek) =>
    formatWeekNumber(Number(yearWeek.slice(4)))
  );

  const options = {
    chart: {
      type: 'column',
      zoomType: 'x',
    },
    title: {
      text: 'Volume semaine tous sports',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories,
      title: {
        text: 'week',
      },
    },
    yAxis: {
      title: {
        text: dataTypeLabels[activityTotal],
      },
      stackLabels: {
        enabled: true,
      },
    },
    plotOptions: {
      column: {
        stacking,
      },
    },
    tooltip: {
      valueSuffix: ` ${dataTypeUnits[activityTotal]}`,
      valueDecimals: 2,
    },
    series: [
      {
        name: 'cycling',
        data: Object.values(ridingStats),
        color: orange,
      },
      {
        name: 'running',
        data: Object.values(runningStats),
        color: green,
      },
      {
        name: 'swimming',
        data: Object.values(swimmingStats),
        color: blue,
      },
      {
        name: 'mean',
        color: black,
        data: [
          [0, mean],
          [nbWeeks, mean],
        ],
        ...meanLineStyle,
      },
    ],
  };

  return (
    <div id="triathlon">
      <Head title="Strava Dashboard - triathlon" />
      <div className={styles.radioButtonsWrapper}>
        <ActivityTotalOptions
          activityTotal={activityTotal}
          handleRadioChangeActivityTotal={handleRadioChangeActivityTotal}
          dataTypes={dataTypes}
        />
        <StackingOptions
          stacking={stacking}
          handleRadioChangeStacking={handleRadioChangeStacking}
        />
      </div>
      <div className={styles.total}>
        <b>{`Average ${dataTypeLabels[activityTotal]}: `}</b>
        {`${mean}`}
      </div>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default TriathlonChart;
